/* eslint-disable hs-react-native/no-web-globals */

import { TICKET_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { useDataFetchingClient } from 'data-fetching-client';
import { bulkFetchValidatedViewMembers } from 'find-and-filter-data/view-members-data/public';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
// eslint-disable-next-line conversations/no-private-internals-import
import { fetchAndCacheCustomView } from '../../../spaces/_internal/utils/fetchAndCacheCustomView';
// eslint-disable-next-line conversations/no-private-internals-import
import { fetchUpdatedViewCounts } from '../../../spaces/_internal/utils/fetchUpdatedViewCounts';
// eslint-disable-next-line conversations/no-private-internals-import
import { updateViewTableCount } from '../../../view-members-data/_internal/help-desk-view-member/helpDeskViewMemberActions';
import { usePageVisibility } from './usePageVisibility';
export const REALTIME_BUFFER_INTERVAL = 5000;
export const LOW_PRIORITY_REALTIME_BUFFER_INTERVAL = 30000;
const REALTIME_UPDATE_LIMIT = 100;
// Every 6th flush (~30s), flush pending low priority updates
const LOW_PRIORITY_FACTOR = 6;

/**
 * Provide a backdoor for unit tests to flush the realtime buffers while
 * avoiding jasmine.clock(), which causes subtle, difficult to debug problems
 * with promises, MSW, and library internals.
 */
function setRealtimeBufferInterval(callback, timeout) {
  if (window.jasmine) {
    window.flushRealtimeBuffers = callback;
  }
  return setInterval(callback, timeout);
}
function clearRealtimeBufferInterval(intervalId) {
  if (window.jasmine) {
    window.flushRealtimeBuffers = undefined;
  }
  clearInterval(intervalId);
}
const defaultMessageBuffer = {
  added: new Set(),
  removed: new Set(),
  updated: new Set(),
  viewCountUpdated: new Set(),
  viewStatusChanged: new Set()
};

/**
 * Buffers incoming realtime messages to be flushed at a regular interval
 */
export function useRealtimeBuffer({
  handleRealtimeUpdate,
  isBoardView,
  priorityViewIds,
  quickFilters,
  refetchMembers,
  shouldRefetchMembers,
  viewId
}) {
  const dispatch = useDispatch();
  const dataFetchingClient = useDataFetchingClient();
  const highPriorityIntervalId = useRef(0);
  const messageBuffer = useRef(defaultMessageBuffer);
  const flushCounter = useRef(0);
  const isPageVisible = usePageVisibility();

  // Reset view member buffers when changing views, as we only store one view's members at a time.
  useEffect(() => {
    messageBuffer.current.added = new Set();
    messageBuffer.current.removed = new Set();
    messageBuffer.current.updated = new Set();
  }, [viewId]);
  const flushMessages = useCallback(async () => {
    var _priorityViewIds$some;
    const {
      added,
      removed,
      updated,
      viewCountUpdated,
      viewStatusChanged
    } = messageBuffer.current;
    flushCounter.current++;

    // Realtime updates do not respect CRM filters. If a view count update is
    // not received for current view, we still need to fetch the table count if
    // a member update message is received while filters are applied
    if (quickFilters.length && updated.size) {
      viewCountUpdated.add(+viewId);
    }
    const updatedObjectIds = new Set([...added, ...updated]);
    const memberUpdateCount = new Set([...updatedObjectIds, ...removed]).size;

    // If the number of realtime updates to validate exceeds the limit,
    // refetch all members and clear buffer
    if (memberUpdateCount >= REALTIME_UPDATE_LIMIT || shouldRefetchMembers !== null && shouldRefetchMembers !== void 0 && shouldRefetchMembers(messageBuffer.current)) {
      refetchMembers();
      messageBuffer.current.added = new Set();
      messageBuffer.current.updated = new Set();
      messageBuffer.current.removed = new Set();
    }
    // Otherwise, validate the updated members and dispatch update action
    else if (updatedObjectIds.size) {
      // If there are duplicate ids across `added`/`updated` and `removed`,
      // delete them from `removed` and rely on the bulk fetch api to tell us
      // whether to remove them from the view. This is necessary because we
      // don't know the order in which these messages arrived (they may have
      // even come out of order) and we must use backend as the source of truth.
      for (const id of updatedObjectIds) {
        removed.delete(id);
      }

      // Clear the buffer prior to the validation call to ensure that we don't
      // accidentally clear realtime updates that happened during validation
      messageBuffer.current.added = new Set();
      messageBuffer.current.removed = new Set();
      messageBuffer.current.updated = new Set();

      // Use new bulk fetch endpoint to validate view members
      const results = await bulkFetchValidatedViewMembers({
        isBoardView,
        objectIds: [...updatedObjectIds],
        objectTypeId: TICKET_TYPE_ID,
        viewId: +viewId,
        quickFilters
      });
      const updatedTickets = [];
      for (const ticket of results.viewMembers) {
        updatedTickets.push(ticket);
        updatedObjectIds.delete(ticket.objectKey.objectId);
      }

      // Any updatedObjectIds missing in the bulk fetch response presumably
      // failed the view membership check, and should be removed from state.
      for (const id of updatedObjectIds) {
        removed.add(id);
      }
      handleRealtimeUpdate({
        updated: updatedTickets,
        removed: [...removed],
        viewId
      });
    }
    // If no updates, just remove the removed tickets
    else if (removed.size) {
      handleRealtimeUpdate({
        removed: [...removed],
        viewId
      });
      messageBuffer.current.removed = new Set();
    }
    const hasHighPriorityViewCountUpdate = (_priorityViewIds$some = priorityViewIds === null || priorityViewIds === void 0 ? void 0 : priorityViewIds.some(id => viewCountUpdated.has(+id))) !== null && _priorityViewIds$some !== void 0 ? _priorityViewIds$some : false;

    // Fetch updated view counts for all views that have been updated
    if (hasHighPriorityViewCountUpdate) {
      messageBuffer.current.viewCountUpdated = new Set();
      const currentViewIdIsIncluded = viewCountUpdated.has(+viewId);
      const result = await fetchUpdatedViewCounts(Object.assign({
        client: dataFetchingClient,
        viewIds: [...viewCountUpdated]
      }, currentViewIdIsIncluded ? {
        currentCustomViewId: +viewId,
        quickFilters
      } : {})).catch(() => ({
        currentViewTableCount: undefined
      }));
      if (currentViewIdIsIncluded && result.currentViewTableCount !== undefined) {
        dispatch(updateViewTableCount({
          viewId: viewId,
          currentViewTableCount: result.currentViewTableCount
        }));
      }
    }
    // Flush pending low priority view count updates if not already flushing high priority updates
    else if (viewCountUpdated.size && flushCounter.current % LOW_PRIORITY_FACTOR === 0) {
      messageBuffer.current.viewCountUpdated = new Set();

      // Fetch updated view counts for all views that have been updated
      fetchUpdatedViewCounts({
        viewIds: [...viewCountUpdated],
        client: dataFetchingClient
      }).catch(() => {
        //ignore errors
      });
    }
    if (viewStatusChanged.size) {
      const viewIds = Array.from(viewStatusChanged);
      messageBuffer.current.viewStatusChanged = new Set();
      for (const changedViewId of viewIds) {
        fetchAndCacheCustomView({
          client: dataFetchingClient,
          viewIdToFetch: changedViewId
        }).catch(() => {
          //ignore errors
        });
      }
    }
  }, [dataFetchingClient, dispatch, handleRealtimeUpdate, isBoardView, priorityViewIds, quickFilters, refetchMembers, shouldRefetchMembers, viewId]);

  //Handle high priority updates
  useEffect(() => {
    if (isPageVisible) {
      highPriorityIntervalId.current = setRealtimeBufferInterval(flushMessages, REALTIME_BUFFER_INTERVAL);
    }
    return () => clearRealtimeBufferInterval(highPriorityIntervalId.current);
  }, [flushMessages, isPageVisible]);
  return useMemo(() => ({
    add(...added) {
      for (const id of added) {
        messageBuffer.current.added.add(id);
      }
    },
    remove(...removed) {
      for (const id of removed) {
        messageBuffer.current.removed.add(id);
      }
    },
    update(...updated) {
      for (const id of updated) {
        messageBuffer.current.updated.add(id);
      }
    },
    viewCountUpdate(...viewCountsUpdated) {
      for (const id of viewCountsUpdated) {
        messageBuffer.current.viewCountUpdated.add(id);
      }
    },
    viewStatusChanged(...viewIds) {
      for (const id of viewIds) {
        messageBuffer.current.viewStatusChanged.add(id);
      }
    }
  }), []);
}